/**
 * Patch firewall: validates and sanitizes builder patches for safety.
 * Use server-side (builder-chat-openai) and client-side (before applying to FS).
 */

export const MAX_ITEMS = 25;
export const MAX_TOTAL_CHARS = 120_000;
export const MAX_FILE_CHARS = 30_000;

const ALLOWED_EXTENSIONS = new Set([".ts", ".tsx", ".css", ".json", ".md", ".sql"]);
const DENYLIST = new Set(["src/main.tsx", "index.html"]);
/** Scaffold files that AI should not overwrite. Exact-match (not prefix). */
const SCAFFOLD_PROTECTED_FILES = new Set([
  // src/lib/ scaffold files
  "src/lib/cn.ts",
  "src/lib/utils.ts",
  "src/lib/supabase.ts",
  "src/lib/database.ts",
  "src/lib/storage.ts",
  // src/components/ui/ scaffold files
  "src/components/ui/button.tsx",
  "src/components/ui/card.tsx",
  "src/components/ui/input.tsx",
  "src/components/ui/textarea.tsx",
  "src/components/ui/badge.tsx",
  "src/components/ui/table.tsx",
  "src/components/ui/select.tsx",
  "src/components/ui/dialog.tsx",
  "src/components/ui/tabs.tsx",
  "src/components/ui/alert.tsx",
  "src/components/ui/avatar.tsx",
  "src/components/ui/separator.tsx",
  "src/components/ui/index.ts",
]);
const DAYONE_ALLOWED = ".dayone/builder-rules.md";
const DELETE_ALLOWED_PREFIX = "src/screens/";
export const MAX_PATH_LEN = 200;

/** Shared path normalization (trim, backslash→/, strip leading .//). Case-preserving. */
export function normalizePathForPatch(p: string): string {
  return String(p ?? "")
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\.\//, "")
    .replace(/^\/+/, "");
}

function normalizePath(p: string): string {
  return normalizePathForPatch(p);
}

export type PatchItem = {
  op: "upsert" | "delete";
  path: string;
  content?: string;
};

export type RejectedItem = {
  path: string;
  reason: string;
  index?: number;
};

export type ValidateBuilderPatchResult = {
  patch: PatchItem[];
  rejected: RejectedItem[];
  acceptedCount: number;
  rejectedCount: number;
  totalChars: number;
};

export type RawPatchInput = {
  path?: string;
  filePath?: string;
  op?: "upsert" | "delete";
  action?: string;
  content?: string;
};

/** Convert legacy action to op. */
function toOp(action: string): "upsert" | "delete" | null {
  const a = String(action || "").toLowerCase();
  if (a === "delete" || a === "remove") return "delete";
  if (a === "create" || a === "update" || a === "modify" || a === "edit" || a === "upsert") return "upsert";
  return null;
}

function validatePath(path: string): string | null {
  const n = normalizePath(path);
  const lower = n.toLowerCase();
  if (!n) return "path empty";
  for (let j = 0; j < n.length; j++) if (n.charCodeAt(j) < 32) return "Path contains control characters";
  if (n.length > MAX_PATH_LEN) return "Path too long";
  if (n.includes("..")) return "path contains '..'";
  if (n.includes("\\")) return "path contains backslash";
  if (n.includes("//")) return "path contains '//'";
  if (/^(https?|data|file):/i.test(n)) return "path is URL scheme";
  if (!lower.startsWith("src/") && !lower.startsWith(".dayone/") && !lower.startsWith("supabase/")) return "path must start with src/, .dayone/, or supabase/";
  if (lower.startsWith("supabase/") && !lower.startsWith("supabase/migrations/")) return "supabase/ path only allowed for supabase/migrations/*";
  if (lower.startsWith(".dayone/") && lower !== DAYONE_ALLOWED.toLowerCase()) return "Unsupported .dayone path";
  if (DENYLIST.has(lower)) return "path is denylisted (index.html, src/main.tsx)";
  if (SCAFFOLD_PROTECTED_FILES.has(lower)) return "Path is a scaffold file (protected from overwrites)";
  const ext = n.includes(".") ? "." + n.split(".").pop()!.toLowerCase() : "";
  if (!ALLOWED_EXTENSIONS.has(ext)) return `extension not allowed (use .ts .tsx .css .json .md .sql)`;
  return null;
}

/**
 * Validate and sanitize a builder patch.
 * Returns accepted items + list of rejections for debug/UI.
 */
export function validateBuilderPatch(
  raw: RawPatchInput[] | { items?: RawPatchInput[]; patch?: RawPatchInput[] },
  opts?: { maxItems?: number; maxTotalChars?: number; maxFileChars?: number }
): ValidateBuilderPatchResult {
  const items = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.items)
    ? raw.items
    : Array.isArray(raw?.patch)
    ? raw.patch
    : [];

  const maxItems = opts?.maxItems ?? MAX_ITEMS;
  const maxTotalChars = opts?.maxTotalChars ?? MAX_TOTAL_CHARS;
  const maxFileChars = opts?.maxFileChars ?? MAX_FILE_CHARS;

  const rejected: RejectedItem[] = [];
  const accepted: PatchItem[] = [];
  const seenPaths = new Set<string>();
  let totalChars = 0;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const n = normalizePath(item?.path ?? item?.filePath ?? "");
    const op = item?.op ? (item.op === "delete" ? "delete" : "upsert") : toOp(item?.action ?? "");
    const content = typeof item?.content === "string" ? item.content : "";

    if (!n) {
      rejected.push({ path: "(empty)", reason: "path empty", index: i });
      continue;
    }
    if (!op) {
      rejected.push({ path: n, reason: `invalid op/action: ${item?.action ?? item?.op ?? "missing"}`, index: i });
      continue;
    }

    const pathErr = validatePath(item?.path ?? item?.filePath ?? "");
    if (pathErr) {
      rejected.push({ path: n, reason: pathErr, index: i });
      continue;
    }

    if (op === "delete" && !n.toLowerCase().startsWith(DELETE_ALLOWED_PREFIX)) {
      rejected.push({ path: n, reason: "Delete restricted to src/screens/", index: i });
      continue;
    }

    const nLower = n.toLowerCase();
    if (seenPaths.has(nLower)) {
      rejected.push({ path: n, reason: "duplicate path in patch (only one mutation per path)", index: i });
      continue;
    }

    if (op === "upsert") {
      const len = content.length;
      if (len > maxFileChars) {
        rejected.push({
          path: n,
          reason: `content exceeds max ${maxFileChars} chars (got ${len})`,
          index: i,
        });
        continue;
      }
      totalChars += len;
      if (totalChars > maxTotalChars) {
        rejected.push({
          path: n,
          reason: `total patch size would exceed ${maxTotalChars} chars`,
          index: i,
        });
        totalChars -= len;
        continue;
      }
    }

    if (accepted.length >= maxItems) {
      rejected.push({ path: n, reason: `exceeds max ${maxItems} items`, index: i });
      continue;
    }

    seenPaths.add(nLower);
    const finalPath = nLower.startsWith("src/") || nLower.startsWith(".dayone/") || nLower.startsWith("supabase/") ? n : "src/" + n.replace(/^src\/?/i, "");
    accepted.push({ op, path: finalPath, content: op === "upsert" ? content : undefined });
  }

  return {
    patch: accepted,
    rejected,
    acceptedCount: accepted.length,
    rejectedCount: rejected.length,
    totalChars,
  };
}

/**
 * Convert validated PatchItem[] to format expected by guardAndCoalescePatches / applyEdits.
 */
export function patchItemsToEdits(
  items: PatchItem[]
): Array<{ filePath: string; action: "create" | "modify" | "delete"; content?: string }> {
  return items.map((p) => ({
    filePath: p.path,
    action: p.op === "delete" ? "delete" : ("modify" as const),
    content: p.op === "upsert" ? (p.content ?? "") : undefined,
  }));
}

/**
 * Convert edits (filePath, action, content) to RawPatchInput for validation.
 */
export function editsToRaw(edits: Array<{ filePath?: string; path?: string; action?: string; content?: string }>): RawPatchInput[] {
  return edits.map((e) => ({
    path: e.filePath ?? e.path,
    action: e.action,
    content: e.content,
  }));
}
